/**
 * StatusBadge.tsx
 *
 * Footer status indicator. Polls /api/status (which proxies BetterStack) and
 * renders a coloured, pulsing dot + the human-readable status description as
 * a link to the public status page.
 *
 * Why this component exists:
 *   The previous implementation embedded BetterStack's hosted badge in a
 *   cross-origin <iframe>. Because the iframe document is cross-origin, the
 *   parent page can't inject CSS into it (contentDocument is null) — see
 *   Issue history. Rendering our own badge from the same JSON the iframe is
 *   driven off lets us style it freely and animate the dot to signal that
 *   the indicator is live (rather than a stale screenshot).
 *
 * Polling:
 *   30s interval matches the BetterStack uptime check cadence. We refetch
 *   on mount and every 30s thereafter; the backend caches with a 60s TTL so
 *   the upstream isn't hit on every poll. We pause the timer while the tab
 *   is hidden because nobody is reading the badge anyway, and waking up to
 *   a flurry of catch-up fetches isn't useful.
 *
 * Accessibility:
 *   - `<a>` to the public status page so users can drill in.
 *   - Pulsing dot is `aria-hidden`; the description text is the accessible
 *     name. Pulse is decorative — a screen reader hearing "All systems
 *     operational" already has the information. The `aria-live="polite"`
 *     on the wrapper announces transitions (operational → degraded) without
 *     interrupting whatever the user is doing.
 *   - `prefers-reduced-motion` disables the pulse.
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import {
  fetchPlatformStatus,
  type PlatformStatus,
  type PlatformStatusKind,
} from '../../services/api/statusApi';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CloseIcon from '@mui/icons-material/Close';
import ConstructionIcon from '@mui/icons-material/Construction';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

// ── Constants ────────────────────────────────────────────────────────────────

/** How often the client polls /api/status. Backend cache TTL is 60s, so this
 *  is an upper bound on how stale the dot's colour can be (typically faster
 *  because tabs that just refocused refetch immediately). */
const POLL_INTERVAL_MS = 30_000;

/** Public status-page URL — where the dot links out to so users can read
 *  the incident detail. Hardcoded rather than env-driven because (a) it's
 *  a public URL anyway and (b) the API constant points at the same host. */
const STATUS_PAGE_URL = 'https://status.writevellum.com/';

/** Token + RGB triplet pairs per status. Tokens are used for the foreground;
 *  the RGB triplet feeds the pulse keyframes (which need a literal colour to
 *  fade-out via rgba()). Keeping both here makes adding a new status a
 *  one-line change. */
const STATUS_PALETTE: Record<
  PlatformStatusKind,
  {
    token: string;
    rgb: string;
    Icon: JSX.Element;
  }
> = {
  operational: {
    token: 'var(--color-success, #22c55e)',
    rgb: '34, 197, 94',
    Icon: <CheckIcon />,
  },
  degraded: {
    token: 'var(--color-warning, #f59e0b)',
    rgb: '245, 158, 11',
    Icon: <PriorityHighIcon />,
  },
  down: {
    token: 'var(--color-danger, #ef4444)',
    rgb: '239, 68, 68',
    Icon: <CloseIcon />,
  },
  maintenance: {
    token: 'var(--color-info, #3b82f6)',
    rgb: '59, 130, 246',
    Icon: <ConstructionIcon />,
  },
  unknown: {
    token: 'var(--color-text-disabled, #6b7280)',
    rgb: '107, 114, 128',
    Icon: <QuestionMarkIcon />,
  },
};

// ── Animations ───────────────────────────────────────────────────────────────

/** The dot's own size doesn't change — the pulse is a halo via box-shadow,
 *  which leaves the inline layout undisturbed (animating width/height would
 *  shift the description text every cycle). The shadow fades from solid at
 *  0% to transparent at 100% across an 8px expansion radius. */
const pulse = (rgb: string) => keyframes`
  0%   { box-shadow: 0 0 0 0   rgba(${rgb}, 0.55); }
  70%  { box-shadow: 0 0 0 8px rgba(${rgb}, 0);    }
  100% { box-shadow: 0 0 0 0   rgba(${rgb}, 0);    }
`;

// ── Styled atoms ─────────────────────────────────────────────────────────────

const Wrap = styled.a<{ $stale?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border-radius: var(--radius-full);
  font-size: 13px;
  color: var(--color-text-secondary);
  text-decoration: none;
  white-space: nowrap;
  transition:
    color var(--duration-shortest) ease,
    background var(--duration-shortest) ease;
  /* When the API is serving its last-known-good fallback, dim the badge
   * slightly so a stuck status doesn't masquerade as live. */
  opacity: ${({ $stale }) => ($stale ? 0.65 : 1)};

  &:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

const Dot = styled.span<{ $token: string; $rgb: string; $animate: boolean }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: -4px; /* visually center the dot with the text (line-height of 13px + 4px padding ≈ 21px total height) */
  background: ${({ $token }) => $token};
  /* Animation is opt-out via prefers-reduced-motion (passed in as $animate). */
  animation: ${({ $rgb, $animate }) => ($animate ? pulse($rgb) : 'none')} 1.8s ease-out infinite;

  svg {
    height: 90%;
    width: 90%;
    color: white;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Label = styled.span<{ $rgb: string }>`
  /* Keep the description on a single line; if the parent is too narrow the
   * badge will be visually clipped via overflow on the parent rather than
   * wrapping inside the badge (which would look like a layout bug). */
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
  color: ${({ $rgb }) =>
    `rgb(${$rgb})`}; /* default to the "unknown" colour while loading to avoid a jarring flash from no-dot to coloured-dot when the fetch resolves */
`;

// ── Component ────────────────────────────────────────────────────────────────

export default function StatusBadge() {
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  // useRef + cleanup-on-unmount guards against a late-arriving fetch updating
  // state on an unmounted instance during HMR or Suspense suspends.
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;

    const load = () => {
      fetchPlatformStatus()
        .then((s) => {
          if (aliveRef.current) setStatus(s);
        })
        .catch(() => {
          // Backend always succeeds with at least { status: 'unknown' }, so
          // landing in this branch means a network-level failure (offline,
          // DNS, etc.). Surface the same "unknown" state so the badge
          // doesn't blink out and reflow the footer.
          if (aliveRef.current) {
            setStatus({
              status: 'unknown',
              description: 'Status check unavailable',
            });
          }
        });
    };

    load();

    // Poll while visible. Pausing on hidden tabs avoids the "thundering herd
    // on focus" pattern where every backgrounded tab fires a fetch the
    // moment the user comes back to the browser.
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_INTERVAL_MS);

    // Refetch immediately when the user returns to a previously hidden tab
    // so they see a fresh value within ~50ms instead of waiting up to 30s.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      aliveRef.current = false;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (!status) {
    // Reserve the visual footprint with a placeholder so the footer doesn't
    // shift the moment the first fetch resolves. width:0 collapses the dot;
    // the wrap keeps the same height as the loaded state (line-height of
    // 13px + 4px*2 padding ≈ 21px).
    return <Wrap aria-hidden="true" tabIndex={-1} style={{ visibility: 'hidden' }} />;
  }

  const palette = STATUS_PALETTE[status.status] || STATUS_PALETTE.unknown;

  return (
    <Wrap
      href={STATUS_PAGE_URL}
      target="_blank"
      rel="noopener noreferrer"
      $stale={status.stale}
      aria-live="polite"
      aria-label={`Service status: ${status.description}${status.stale ? ' (cached)' : ''}`}
    >
      <Dot
        $token={palette.token}
        $rgb={palette.rgb}
        $animate={status.status !== 'unknown'}
        aria-hidden="true"
      >
        {palette.Icon}
      </Dot>
      <Label $rgb={palette.rgb}>{status.description}</Label>
    </Wrap>
  );
}
